import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import classNames from 'classnames'

import Loader from '../components/Loader'
import { Link } from 'react-router-dom'
import Highlight from 'react-highlighter'

import * as UserActions from 'tw-oi-core/actions/UserActions'

import { ROUTE, UI } from '../config'
import Icon from '../components/Icon'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import { isFolder, isDitaTopic, isVideo, isPdf, isTopic, isArticlePublication } from '../extras/utils'
import { trackDownloadOwnersManualPDF } from 'tw-oi-core/services/analytics'
import { isOffline } from '../services/offlineDetect'

import '../styles/ContentList.scss'

class ContentList extends Component {
  static propTypes = {
    topics: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        id: PropTypes.string,
        icon: PropTypes.object,
        type: PropTypes.string,
        resourceKey: PropTypes.string,
        link: PropTypes.string,
        bodyHtml: PropTypes.object,
      })
    ),
    searchQuery: PropTypes.string,
    emptyText: PropTypes.string,
    baseRoute: PropTypes.string,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'browse-guide']),
    onClick: PropTypes.func,
    downloadedCategories: PropTypes.object,
    onRemoveDownloadedCategory: PropTypes.func,
    downloadedPdfResourceKeys: PropTypes.object,
    individuallyDownloadedPdfResourceKeys: PropTypes.object,
    downloadingPdfResourceKeys: PropTypes.object,
    onDownloadComponentManualPdf: PropTypes.func,
    onRemoveComponentManualPdf: PropTypes.func,
    UserActions: PropTypes.shape({
      addRecent: PropTypes.func.isRequired,
    }).isRequired,
    currentYear: PropTypes.string,
    currentModel: PropTypes.string,
    currentBrand: PropTypes.string,
    currentProductType: PropTypes.string,
    currentProductSubtype: PropTypes.string,
  }

  static defaultProps = {
    searchQuery: '',
    variant: 'default',
  }

  renderPdfListIcon() {
    return (
      <div className="icon icon-list-pdf">
        <PictureAsPdfOutlinedIcon sx={{ fontSize: 24 }} />
      </div>
    )
  }

  renderPdfDownloadIcon() {
    return (
      <div className="icon icon-download-pdf">
        <FileDownloadOutlinedIcon sx={{ fontSize: 24 }} />
      </div>
    )
  }

  renderPdfDownloadItem = topic => {
    const { currentModel } = this.props

    return (
      <li key={topic.id || topic.title}>
        <div className='item-inner'>
          <div className='item-iconbar'>{topic.icon || this.renderPdfListIcon()}</div>
          <div className='item-title'>{topic.title}</div>
        </div>
        <div className='item-trailing'>
          <a
            href={topic.downloadUrl}
            className='item-download-link'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => trackDownloadOwnersManualPDF(currentModel, topic.language)}
          >
            {UI.DOWNLOAD_PDF}
            {this.renderPdfDownloadIcon()}
          </a>
        </div>
      </li>
    )
  }

  renderComponentManualPdfItem = topic => {
    const {
      downloadedPdfResourceKeys,
      individuallyDownloadedPdfResourceKeys,
      downloadingPdfResourceKeys,
      onDownloadComponentManualPdf,
      onRemoveComponentManualPdf,
    } = this.props
    const link = this.getLink(topic)
    const resourceKey = topic.resourceKey
    const downloaded = downloadedPdfResourceKeys && downloadedPdfResourceKeys.has(resourceKey)
    const individuallyDownloaded = individuallyDownloadedPdfResourceKeys
      && individuallyDownloadedPdfResourceKeys.has(resourceKey)
    const downloading = downloadingPdfResourceKeys && downloadingPdfResourceKeys.has(resourceKey)
    const showOfflineDownload = !isOffline()

    return (
      <li key={topic.id || topic.title}>
        <Link className='item-inner' to={link} onClick={e => this.handleClick(e, topic)}>
          <div className='item-iconbar'>{topic.icon || this.renderPdfListIcon()}</div>
          <div className='item-title'>{topic.title}</div>
        </Link>
        {showOfflineDownload && (
          <div className='item-trailing'>
            {downloaded ? (
              individuallyDownloaded ? (
                <button
                  type="button"
                  className="item-downloaded-marker"
                  title="Remove from offline storage"
                  aria-label={`Remove ${topic.title} from offline storage`}
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (onRemoveComponentManualPdf) onRemoveComponentManualPdf(topic)
                  }}
                >
                  <div className="icon icon-cloud-check">
                    <CloudDoneOutlinedIcon />
                  </div>
                </button>
              ) : (
                <span className="item-downloaded-marker item-downloaded-marker--static" aria-hidden="true">
                  <div className="icon icon-cloud-check">
                    <CloudDoneOutlinedIcon />
                  </div>
                </span>
              )
            ) : (
              <button
                type="button"
                className={classNames('item-offline-download', downloading && 'is-downloading')}
                title="Save for offline use"
                aria-label={`Save ${topic.title} for offline use`}
                disabled={downloading}
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (onDownloadComponentManualPdf) onDownloadComponentManualPdf(topic)
                }}
              >
                <CloudDownloadOutlinedIcon />
              </button>
            )}
          </div>
        )}
      </li>
    )
  }

  renderItemIcon = item => {
    if (item.icon) {
      return item.icon
    }
    if (isFolder(item)) {
      return <Icon type='folder' />
    } else if (isDitaTopic(item) || isTopic(item) || isArticlePublication(item)) {
      return <Icon type='list-topic' />
    } else if (isVideo(item)) {
      return <Icon type='list-film' />
    } else if (isPdf(item)) {
      return this.renderPdfListIcon()
    }
  }

  handleClick = (e, item) => {
    const { onClick } = this.props
    onClick && onClick(item)
  }

  getLink = item => {
    return (
      item.link ||
      `${this.props.baseRoute}${isFolder(item) ? ROUTE.FOLDER : ROUTE.TOPIC}/${encodeURIComponent(item.resourceKey)}`
    )
  }

  isItemDownloaded = (topic) => {
    const { downloadedCategories } = this.props
    if (!downloadedCategories || !downloadedCategories.size) return false
    return downloadedCategories.has(topic.title)
  }

  handleRemoveDownloaded = (e, topic) => {
    e.preventDefault()
    e.stopPropagation()
    const { onRemoveDownloadedCategory } = this.props
    if (onRemoveDownloadedCategory) {
      onRemoveDownloadedCategory(topic.title)
    }
  }

  render() {
    const { topics, searchQuery, variant } = this.props
    const isBrowseGuide = variant === 'browse-guide'

    if (topics === null || topics.length === 0) {
      return (
        <Loader type='status' className='inverse'>
          {this.props.emptyText || ''}
        </Loader>
      )
    }

    const topicElements = topics.map(topic => {
      if (isBrowseGuide && topic.isComponentManualPdf) {
        return this.renderComponentManualPdfItem(topic)
      }

      if (isBrowseGuide && topic.isPdfDownload && topic.downloadUrl) {
        return this.renderPdfDownloadItem(topic)
      }

      const link = this.getLink(topic)
      const downloaded = isBrowseGuide && this.isItemDownloaded(topic)

      return (
        <li key={topic.id || topic.title}>
          <Link className='item-inner' to={link} onClick={e => this.handleClick(e, topic)}>
            <div className='item-iconbar'>{this.renderItemIcon(topic)}</div>

            <div className='item-title'>
              {searchQuery ? (
                <Highlight className='highlighted' matchElement='mark' search={searchQuery}>
                  {topic.title}
                </Highlight>
              ) : (
                topic.title
              )}
            </div>
          </Link>
          {isBrowseGuide && (
            <div className='item-trailing'>
              {downloaded && (
                <button
                  type="button"
                  className="item-downloaded-marker"
                  title="Remove from offline storage"
                  aria-label={`Remove ${topic.title} from offline storage`}
                  onClick={e => this.handleRemoveDownloaded(e, topic)}
                >
                  <div className="icon icon-cloud-check">
                    <CloudDoneOutlinedIcon />
                  </div>
                </button>
              )}
              <Link className='item-chevron' to={link} onClick={e => this.handleClick(e, topic)} aria-label={topic.title}>
                <Icon type='arrow-right' />
              </Link>
            </div>
          )}
        </li>
      )
    })

    return (
      <ul className={classNames('ContentList', isBrowseGuide && 'BrowseGuideList', this.props.className)}>
        {topicElements}
      </ul>
    )
  }
}

function mapStateToProps({ vehicle }) {
  const { currentYear, currentModel, currentBrand, currentProductType, currentProductSubtype } = vehicle
  return { currentYear, currentModel, currentBrand, currentProductType, currentProductSubtype }
}

function mapDispatchToProps(dispatch) {
  return {
    UserActions: bindActionCreators(UserActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentList)
